import React, { useEffect, useState } from 'react';
import * as API from '../api.js';
import LoaderComponent from '../ReusableComponents/LoaderComponent.js';
import { Popup } from '../ReusableComponents/reusableFunctions.js';
import Button from '../ReusableComponents/Button/Button.js';
import editActive from '../Icon/editActive.svg';

const MONTHS = [
    { name: 'January', value: 1 },
    { name: 'February', value: 2 },
    { name: 'March', value: 3 },
    { name: 'April', value: 4 },
    { name: 'May', value: 5 },
    { name: 'June', value: 6 },
    { name: 'July', value: 7 },
    { name: 'August', value: 8 },
    { name: 'September', value: 9 },
    { name: 'October', value: 10 },
    { name: 'November', value: 11 },
    { name: 'December', value: 12 }
];

const getLastDayOfMonth = (month, year = new Date().getFullYear() + 1) => {
    return new Date(year, month, 0).getDate();
};

const UserSettings = ({ user }) => {
    const [loading, setLoading] = useState(false);
    const [sprayedAreaByDistance, setSprayedAreaByDistance] = useState(false);
    const [lessThan, setLessThan] = useState(0);
    const [excludeOption, setExcludeOption] = useState(false);
    const [financialYearStart, setFinancialYearStart] = useState({ month: MONTHS[3], day: 1 }); // April 1
    const [financialYearEnd, setFinancialYearEnd] = useState({ month: MONTHS[2], day: 31 });   // March 31
    const [saving, setSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [originalSettings, setOriginalSettings] = useState(null);

    const handleLessThanChange = (e) => {
        const val = e.target.value;
        if (!val) {
            setLessThan('');
            return;
        }

        const numVal = Number(val);

        if (numVal < 0) {
            setLessThan(0);
            Popup.alert("ERROR", "Flight time must be at least 1 second.");
        } else if (numVal > 60) {
            setLessThan(lessThan);
            Popup.alert("ERROR", "Flight time must not exceed 60 seconds.");
        } else {
            setLessThan(numVal);
        }
    };

    const calculateFinancialYearEnd = (startMonthVal, startDayVal) => {
        const currentYear = new Date().getFullYear();
        const startDate = new Date(currentYear, startMonthVal - 1, startDayVal);
        const endDate = new Date(startDate);
        endDate.setFullYear(endDate.getFullYear() + 1);
        endDate.setDate(endDate.getDate() - 1);

        const endMonth = MONTHS.find(m => m.value === endDate.getMonth() + 1);
        return { month: endMonth, day: endDate.getDate() };
    };

    const handleFinancialYearStartChange = (monthValue) => {
        const startDay = financialYearStart?.day || 1;
        const startMonth = MONTHS.find(m => m.value === monthValue);

        setFinancialYearStart({ month: startMonth, day: startDay });

        const end = calculateFinancialYearEnd(monthValue, startDay);
        setFinancialYearEnd(end);
    };

    const handleStartDayChange = (dayValue) => {
        const startMonthVal = financialYearStart?.month?.value;
        if (!startMonthVal) return;

        const end = calculateFinancialYearEnd(startMonthVal, Number(dayValue));

        setFinancialYearStart(prev => ({
            ...prev,
            day: Number(dayValue)
        }));
        setFinancialYearEnd(end);
    };

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const { settings } = await API.getUserSettings({});
            const flightSetting = settings.find(s => s.setting_name === 'flightTimeLessThan');
            const sprayedAreaSetting = settings.find(s => s.setting_name === 'sprayedAreaByDistance');
            const financialYear = settings.find(s => s.setting_name === 'financialYear');

            let parsedFlight = { time: 0, value: false };
            let parsedFY = {
                startMonth: { month: MONTHS[3], day: 1 },
                endMonth: { month: MONTHS[2], day: 31 }
            };

            if (flightSetting) {
                parsedFlight = JSON.parse(flightSetting.setting_value);
                setLessThan(Number(parsedFlight.time));
                setExcludeOption(parsedFlight.value);
            }

            if (sprayedAreaSetting) {
                setSprayedAreaByDistance(sprayedAreaSetting.setting_value === 'true');
            }

            if (financialYear) {
                parsedFY = JSON.parse(financialYear.setting_value);
                const startMonth = MONTHS.find(m => m.value === parsedFY.startMonth?.month?.value);
                const endMonth = MONTHS.find(m => m.value === parsedFY.endMonth?.month?.value);

                setFinancialYearStart({
                    month: startMonth || MONTHS[3],
                    day: parsedFY?.startMonth?.day || 1
                });
                setFinancialYearEnd({
                    month: endMonth || MONTHS[2],
                    day: parsedFY?.endMonth?.day || getLastDayOfMonth(endMonth?.value || 3)
                });
            }

            // Set original settings snapshot
            setOriginalSettings({
                sprayedAreaByDistance: sprayedAreaSetting?.setting_value === 'true',
                lessThan: Number(parsedFlight.time),
                excludeOption: parsedFlight.value,
                financialYearStart: {
                    month: MONTHS.find(m => m.value === parsedFY.startMonth?.month?.value) || MONTHS[3],
                    day: parsedFY?.startMonth?.day || 1
                },
                financialYearEnd: {
                    month: MONTHS.find(m => m.value === parsedFY.endMonth?.month?.value) || MONTHS[2],
                    day: parsedFY?.endMonth?.day || getLastDayOfMonth(parsedFY.endMonth?.month?.value || 3)
                }
            });

        } catch (error) {
            console.log('error fetching user settings:', error);
            Popup.alert("ERROR", error.message || "Failed to fetch user settings.");
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async () => {
        if (excludeOption && (lessThan < 1 || lessThan > 60)) {
            Popup.alert("ERROR", "Flight time must be between 1 and 60 seconds.");
            return;
        }

        Popup.alert("WAIT", "Updating settings, please wait...");
        setLoading(true);
        setSaving(true);
        try {
            const settingsPayload = [
                {
                    name: "flightTimeLessThan",
                    value: JSON.stringify({ time: lessThan, value: excludeOption })
                },
                {
                    name: "sprayedAreaByDistance",
                    value: sprayedAreaByDistance
                },
                {
                    name: "financialYear",
                    value: JSON.stringify({
                        startMonth: financialYearStart,
                        endMonth: financialYearEnd
                    })
                }
            ];

            await API.updateUserSettings(settingsPayload);
            Popup.alert("COMPLETE", "Settings updated successfully");
            // Update original settings snapshot
            setOriginalSettings({
                sprayedAreaByDistance,
                lessThan,
                excludeOption,
                financialYearStart,
                financialYearEnd
            });

            setIsEditing(false);
        } catch (error) {
            console.log('error updating user settings:', error);
            Popup.alert("ERROR", error.message || "Something went wrong while updating settings");
        } finally {
            setLoading(false);
            setSaving(false);
        }
    };

    useEffect(() => {
        document.title = 'User Settings - AeroGCS';
        if (user?.userid) fetchSettings();
    }, [user?.userid]);

    if (!user?.username) return null;

    return (
        <div style={{ padding: 24 }}>
            <div style={{ maxWidth: 960, margin: '0 auto' }}>
                {loading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 48 }}>
                        <LoaderComponent />
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                        <div style={{ display: 'flex', alignItems: "center", justifyContent: "space-between" }}>
                            <h2 style={{ fontSize: 20, fontWeight: '600', color: '#081425', marginBottom: 0 }}>General Settings</h2>
                            <div style={{ display: 'flex', gap: "20px", alignItems: 'center' }}>
                                {isEditing ? (
                                    <div style={{ display: "flex", justifyContent: 'center', alignItems: 'center', gap: "15px" }}>
                                        <Button
                                            text="Cancel"
                                            style={{ border: '1px solid #2989cf', backgroundColor: 'white', color: '#2989cf' }}
                                            onClick={() => {
                                                if (originalSettings) {
                                                    setSprayedAreaByDistance(originalSettings.sprayedAreaByDistance);
                                                    setLessThan(originalSettings.lessThan);
                                                    setExcludeOption(originalSettings.excludeOption);
                                                    setFinancialYearStart(originalSettings.financialYearStart);
                                                    setFinancialYearEnd(originalSettings.financialYearEnd);
                                                }
                                                setIsEditing(false);
                                            }}
                                        />
                                        <Button
                                            isDisabled={saving || (excludeOption && lessThan < 1)}
                                            text="Save Changes"
                                            onClick={handleUpdate}
                                            isBtnActive
                                        />
                                    </div>
                                ) : (
                                    <img src={editActive}
                                        style={{ height: "30px", cursor: 'pointer' }}
                                        onClick={() => setIsEditing(true)}
                                    />
                                )}
                            </div>
                        </div>
                        {/* Flight Time Settings */}
                        <div style={{
                            backgroundColor: '#fff',
                            borderRadius: 12,
                            border: '1px solid #e5e7eb',
                            padding: 24
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                                <h2 style={{ fontSize: 20, fontWeight: '600', color: '#081425' }}>Flight Time</h2>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                <div style={{ display: 'flex', justifyContent: "space-between", alignItems: "start" }}>
                                    {excludeOption &&
                                        <div style={{ display: 'flex', gap: 8, flexDirection: "column", width: "60%" }}>
                                            <div style={{ color: "#68717E", fontSize: "14px" }}>Exclude Flights Under (Seconds)</div>
                                            {isEditing ?
                                                <div>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        max="60"
                                                        value={lessThan}
                                                        onChange={handleLessThanChange}
                                                        onKeyDown={(e) => {
                                                            const invalidKeys = ['e', 'E', '+', '-', '.'];
                                                            if (invalidKeys.includes(e.key)) e.preventDefault();
                                                        }}
                                                        style={{
                                                            width: "70%",
                                                            padding: '6px 10px',
                                                            borderRadius: 8,
                                                            border: '1px solid #d1d5db',
                                                            fontSize: 14
                                                        }}
                                                    />
                                                    <div style={{ fontSize: 10, color: '#081425', marginTop: 5, marginBottom: 0 }}>
                                                        <span style={{ marginRight: "10px", color: "#3988F9" }}>Note:</span>
                                                        <span>1 - 60 numbers allowed</span>
                                                    </div>
                                                </div> :
                                                <div style={{ fontSize: 14, color: "#081425" }}>{lessThan}</div>
                                            }
                                        </div>
                                    }
                                    {excludeOption &&
                                        <div style={{ width: "40%" }}>
                                            <p style={{ fontSize: 14, color: '#3988F9', marginTop: 0, marginBottom: 0 }}>
                                                Note:
                                            </p>
                                            <p style={{ fontSize: 14, color: '#081425', marginTop: 0, marginBottom: 0 }}>
                                                Flights under {originalSettings.lessThan || lessThan} seconds will be excluded from reports.
                                            </p>
                                        </div>
                                    }
                                </div>
                                <div style={{ display: 'flex', gap: 8, flexDirection: "column" }}>
                                    <h2 style={{ fontSize: 20, fontWeight: '600', color: '#081425' }}>Short Flights from Reports</h2>
                                    {isEditing ?
                                        <div style={{ display: "flex", gap: '70px' }}>
                                            <label style={{ display: "flex", alignItems: "center", gap: "15px", fontSize: 16 }}>
                                                <input
                                                    type="radio"
                                                    value={true}
                                                    checked={!excludeOption}
                                                    id="excludeFlights"
                                                    onChange={() => setExcludeOption(false)}
                                                    style={{ width: 16, height: 16 }}
                                                />
                                                Include
                                            </label>
                                            <label style={{ display: "flex", alignItems: "center", gap: "15px", fontSize: 16 }}>
                                                <input
                                                    type="radio"
                                                    value={false}
                                                    checked={excludeOption}
                                                    id="excludeFlights"
                                                    onChange={() => setExcludeOption(true)}
                                                    style={{ width: 16, height: 16 }}
                                                />
                                                Exclude
                                            </label>
                                        </div> :
                                        <div>{excludeOption ? 'Exclude' : 'Include'}</div>
                                    }
                                </div>
                            </div>
                        </div>

                        {/* Financial Year Settings */}
                        <div style={{
                            backgroundColor: '#fff',
                            borderRadius: 12,
                            border: '1px solid #e5e7eb',
                            padding: 24
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                                <h2 style={{ fontSize: 20, fontWeight: '600', color: '#081425' }}>Financial Year</h2>
                            </div>

                            <p style={{ color: '#081425', marginBottom: 24, fontSize: "14px" }}>
                                Set your custom financial year period for accurate reporting and analysis.
                            </p>

                            <div style={{ display: 'flex', gap: 50 }}>
                                <div style={{ display: 'flex', alignItems: "start", gap: 20, width: "100%" }}>
                                    <div style={{ width: "100%" }}>
                                        <label style={{ fontSize: 14, fontWeight: 400, color: '#68717E' }}>Start Month</label>
                                        <select
                                            value={financialYearStart.month.value}
                                            onChange={(e) => handleFinancialYearStartChange(Number(e.target.value))}
                                            style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, background: "#FFFFFF", cursor: isEditing ? 'pointer' : 'not-allowed' }}
                                            disabled={!isEditing}
                                        >
                                            {MONTHS.map((month) => (
                                                <option key={month.value} value={month.value}>{month.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div style={{ width: "100%" }}>
                                        <label style={{ fontSize: 14, fontWeight: 400, color: '#68717E' }}>Start Date</label>
                                        <select
                                            value={financialYearStart.day}
                                            onChange={(e) => handleStartDayChange(e.target.value)}
                                            style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, background: "#FFFFFF", cursor: isEditing ? 'pointer' : 'not-allowed' }}
                                            disabled={!isEditing}
                                        >
                                            {Array.from({ length: getLastDayOfMonth(financialYearStart.month.value) }, (_, i) => i + 1).map(day => (
                                                <option key={day} value={day}>{day}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div style={{ margin: "auto" }}>To</div>
                                <div>
                                    <div style={{ display: 'flex', alignItems: "start", gap: 20, width: "100%" }}>
                                        <div style={{ width: "100%" }}>
                                            <label style={{ fontSize: 14, fontWeight: 400, color: '#68717E' }}>End Month</label>
                                            <input
                                                value={financialYearEnd.month.name}
                                                disabled
                                                style={{
                                                    width: '100%',
                                                    padding: '8px 10px',
                                                    border: '1px solid #d1d5db',
                                                    borderRadius: 8,
                                                    backgroundColor: '#EFEFEF',
                                                }}
                                            />
                                        </div>
                                        <div style={{ width: "100%" }}>
                                            <label style={{ fontSize: 14, fontWeight: 400, color: '#68717E' }}>End Date</label>
                                            <input
                                                value={financialYearEnd.day}
                                                disabled
                                                style={{
                                                    width: '100%',
                                                    padding: '8px 10px',
                                                    border: '1px solid #d1d5db',
                                                    borderRadius: 8,
                                                    backgroundColor: '#EFEFEF',
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div style={{ fontSize: 10, color: '#081425', marginTop: 5, marginBottom: 0 }}>
                                        <span style={{ marginRight: "10px", color: "#3988F9" }}>Note:</span>
                                        <span>Automatically sets 12 months from start month and date</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserSettings;